import { Injectable } from '@angular/core';
import { BehaviorSubject, filter, first, map, Observable, of } from 'rxjs';
import { RokezzzSettingService } from './setting.service';
import { Setting } from '../api/setting';
import { RokezzzNotificationService } from '../../notification/services/notification.service';
import { defaultApiFilterConfig } from '../../api/models/default-api-filter-config';
import { ApiResponse } from '../../api/api/api-response';
import { RokezzzImageDataService } from '../../file-upload/services/image-data.service';
import { SettingPrototype } from '../api/setting-prototype';

@Injectable({
    providedIn: 'root'
})
export class RokezzzSettingDataService {
    private readonly settingsSubject: BehaviorSubject<Setting[] | null> = new BehaviorSubject<Setting[] | null>(null);

    private isLoaded: boolean = false;
    private isLoading: boolean = false;

    public constructor(
        protected readonly rokezzzSettingService: RokezzzSettingService,
        protected readonly rokezzzNotificationService: RokezzzNotificationService,
        protected readonly rokezzzImageDataService: RokezzzImageDataService,
    ) { }

    public init(): void {
        this.refreshSettings();
    }

    public getSettings(): BehaviorSubject<Setting[] | null> {
        return this.settingsSubject;
    }

    public getGroupSettings(group: string): Observable<Setting[] | null> {
        if (this.isLoaded) {
            const settings: Setting[] | null = this.settingsSubject.value;

            return of(settings ? settings.filter((setting: Setting) => setting.name && setting.name.startsWith(group)) : null);
        } else {
            return this.settingsSubject.pipe(
                filter((settings: Setting[] | null) => this.isSetting(settings)),
                first(),
                map((settings: Setting[] | null) => settings!.filter((setting: Setting) => setting.name && setting.name.startsWith(group)))
            );
        }
    }

    public getSetting(name: string): Observable<Setting | null> {
        if (this.isLoaded) {
            const settings: Setting[] | null = this.settingsSubject.value;

            return of(settings ? settings.find((setting: Setting) => setting.name === name) || null : null);
        } else if (this.isLoading) {
            return this.settingsSubject.pipe(
                filter((settings: Setting[] | null) => this.isSetting(settings)),
                first(),
                map((settings: Setting[] | null) => settings!.find((setting: Setting) => setting.name === name) || null)
            );
        } else {
            return of(null);
        }
    }

    public clearSettings(): void {
        this.settingsSubject.next(null);
    }

    public getSettingValue(setting: Setting | undefined): string | Record<string, string>[] | object[] {
        if (!setting) {
            return '';
        }

        if (setting.type === 'array') {
            const value: Record<string, string>[] = JSON.parse(setting.value);

            if (setting.config && setting.config.mapping) {
                return value.map((item: Record<string, string>): Record<string, string> => {
                    const mappedItem: Record<string, string> = {};

                    setting.config!.mapping!.forEach((mapConfig: SettingPrototype): void => {
                        if (mapConfig.name) {
                            mappedItem[mapConfig.name] = item[mapConfig.name];
                        }
                    });

                    return mappedItem;
                });
            }

            return value;
        } else if (setting.type === 'object') {
            return JSON.parse(setting.value);
        } else {
            return setting.value;
        }
    }

    public isSetting(settings: Setting[] | null): settings is Setting[] {
        return settings !== null;
    }

    public refreshSettings(): void {
        if (this.isLoading) {
            return;
        }

        this.isLoading = true;
        this.rokezzzSettingService.getCollection(
            '',
            defaultApiFilterConfig,
            (response: ApiResponse<Setting>) => {
                this.settingsSubject.next(response.data);
                this.isLoaded = true;
                this.isLoading = false;
            },
            () => {
                this.isLoading = false;
                this.rokezzzNotificationService.addServerError();
            }
        );
    }
}
